import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  BusinessInfoPayload,
  EInvoiceConfigPayload,
  PaymentConfigPayload,
  ProductRow,
  StaffInvitePayload,
  StorePayload,
} from '@/services/onboarding.service';
import { OnboardingPhase1Step, OnboardingPhase2Step, OnboardingPhase1Step as Phase1Step } from '@erpgold/shared';

export const PHASE1_STEPS: OnboardingPhase1Step[] = [
  Phase1Step.BUSINESS,
  Phase1Step.STORE,
  Phase1Step.STAFF,
  Phase1Step.PRODUCTS,
  Phase1Step.PAYMENT,
  Phase1Step.INVOICE,
];

// ─── Internal camelCase types (mirrors OnboardingProgress from onboarding.service.ts) ───

interface Phase1Data {
  totalSteps: number;
  completedSteps: OnboardingPhase1Step[];
  skippedSteps: OnboardingPhase1Step[];
  isCompleted: boolean;
  completedAt: string | null;
}

interface Phase2Data {
  totalSteps: number;
  completedSteps: OnboardingPhase2Step[];
  skippedSteps: OnboardingPhase2Step[];
  isCompleted: boolean;
  completedAt: string | null;
  dismissedAt: string | null;
}

interface OnboardingProgressState {
  tenantId: string;
  tenantName: string;
  address: string;
  taxCode: string;
  phone: string;
  email: string;
  logo: string;
  cityId: string;
  wardId: string;
  phase1: Phase1Data;
  phase2: Phase2Data;
  hasSampleData?: boolean;
}

export interface OnboardingState {
  /** Hydrate từ `GET /onboarding/progress` lúc mount layout. */
  loaded: boolean;
  /** OnboardingProgress data — keeps API shape intact, no field-flattening. */
  progress: OnboardingProgressState;
  /** Form draft cho mỗi step (giữ tạm trong memory để user back/forward không mất). */
  drafts: {
    business?: BusinessInfoPayload;
    store?: StorePayload;
    staff?: StaffInvitePayload[];
    products?: ProductRow[];
    payment?: PaymentConfigPayload;
    invoice?: EInvoiceConfigPayload;
  };
}

const initialProgress: OnboardingProgressState = {
  tenantId: '',
  tenantName: '',
  address: '',
  taxCode: '',
  phone: '',
  email: '',
  logo: '',
  cityId: '',
  wardId: '',
  phase1: {
    totalSteps: 0,
    completedSteps: [],
    skippedSteps: [],
    isCompleted: false,
    completedAt: null,
  },
  phase2: {
    totalSteps: 0,
    completedSteps: [],
    skippedSteps: [],
    isCompleted: false,
    completedAt: null,
    dismissedAt: null,
  },
  hasSampleData: false,
};

const initialState: OnboardingState = {
  loaded: false,
  progress: initialProgress,
  drafts: {},
};

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    setProgress(
      state,
      action: PayloadAction<{
        tenantId?: string;
        tenantName?: string;
        address?: string;
        taxCode?: string;
        phone?: string;
        email?: string;
        logo?: string;
        cityId?: string;
        wardId?: string;
        phase1: Phase1Data;
        phase2: Phase2Data;
        hasSampleData?: boolean;
      }>,
    ) {
      const p = action.payload;
      // Ensure progress exists (redux-persist may restore corrupted state)
      if (!state.progress) {
        state.progress = { ...initialProgress };
      }
      state.loaded = true;
      state.progress.tenantId = p.tenantId ?? state.progress.tenantId;
      state.progress.tenantName = p.tenantName ?? state.progress.tenantName;
      state.progress.address = p.address ?? state.progress.address;
      state.progress.cityId = p.cityId ?? state.progress.cityId;
      state.progress.wardId = p.wardId ?? state.progress.wardId;
      state.progress.phase1 = p.phase1;
      state.progress.phase2 = p.phase2;
      state.progress.hasSampleData = p.hasSampleData ?? state.progress.hasSampleData ?? false;


      if (action.payload.address !== undefined) {
        state.progress.address = action.payload.address;
      }
      if (action.payload.taxCode !== undefined) {
        state.progress.taxCode = action.payload.taxCode;
      }
      if (action.payload.phone !== undefined) {
        state.progress.phone = action.payload.phone;
      }
      if (action.payload.email !== undefined) {
        state.progress.email = action.payload.email;
      }
      if (action.payload.logo !== undefined) {
        state.progress.logo = action.payload.logo;
      }
      if (action.payload.cityId !== undefined) {
        state.progress.cityId = action.payload.cityId;
      }
      if (action.payload.wardId !== undefined) {
        state.progress.wardId = action.payload.wardId;
      }
    },
    markStepDone(state, action: PayloadAction<OnboardingPhase1Step>) {
      const step = action.payload;
      if (!state.progress?.phase1) return;
      if (!state.progress.phase1.completedSteps.includes(step)) {
        state.progress.phase1.completedSteps.push(step);
      }
      state.progress.phase1.skippedSteps = state.progress.phase1.skippedSteps.filter(
        (s) => s !== step,
      );
    },


    markStepSkipped(state, action: PayloadAction<OnboardingPhase1Step>) {
      const step = action.payload;
      if (!state.progress?.phase1) return;
      if (!state.progress.phase1.skippedSteps.includes(step)) {
        state.progress.phase1.skippedSteps.push(step);
      }
    },

    saveDraft<K extends keyof OnboardingState['drafts']>(
      state: OnboardingState,
      action: PayloadAction<{ key: K; value: OnboardingState['drafts'][K] }>,
    ) {
      state.drafts[action.payload.key] = action.payload.value;
    },

    setCompleted(state) {
      if (!state.progress?.phase1) return;
      state.progress.phase1.isCompleted = true;
    },

    setHasSampleData(state, action: PayloadAction<boolean>) {
      if (!state.progress) return;
      state.progress.hasSampleData = action.payload;
    },

    reset() {
      return initialState;
    },
  },
});

export const onboardingActions = onboardingSlice.actions;
export const onboardingReducer = onboardingSlice.reducer;

// ─── SELECTORS ───────────────────────────────────────────────────────────────────

export const onboardingSelectors = {
  loaded: (s: { onboarding?: OnboardingState }) => s.onboarding?.loaded ?? false,

  // Phase 1 (flat for convenience — mirrors old selectors)
  completedSteps: (s: { onboarding?: OnboardingState }) =>
    s.onboarding?.progress?.phase1?.completedSteps ?? [],
  skippedSteps: (s: { onboarding?: OnboardingState }) =>
    s.onboarding?.progress?.phase1?.skippedSteps ?? [],
  isCompleted: (s: { onboarding?: OnboardingState }) =>
    s.onboarding?.progress?.phase1?.isCompleted ?? false,

  // Tenant info
  tenantName: (s: { onboarding?: OnboardingState }) =>
    s.onboarding?.progress?.tenantName ?? '',
  address: (s: { onboarding?: OnboardingState }) =>
    s.onboarding?.progress?.address ?? '',
  cityId: (s: { onboarding?: OnboardingState }) =>
    s.onboarding?.progress?.cityId ?? '',
  wardId: (s: { onboarding?: OnboardingState }) =>
    s.onboarding?.progress?.wardId ?? '',

  // Draft
  draft: <K extends keyof OnboardingState['drafts']>(key: K) =>
    (s: { onboarding?: OnboardingState }) => s.onboarding?.drafts?.[key],
  taxCode: (s: { onboarding?: OnboardingState }) => s.onboarding?.progress?.taxCode ?? '',
  phone: (s: { onboarding?: OnboardingState }) => s.onboarding?.progress?.phone ?? '',
  email: (s: { onboarding?: OnboardingState }) => s.onboarding?.progress?.email ?? '',
  logo: (s: { onboarding?: OnboardingState }) => s.onboarding?.progress?.logo ?? '',
  /** Step pending kế tiếp (đầu tiên trong PHASE1_STEPS chưa done/skipped). */
  nextPendingStep: (s: { onboarding?: OnboardingState }) => {
    const progress = s.onboarding?.progress;
    if (!progress?.phase1) return null;
    const handled = new Set([
      ...progress.phase1.completedSteps,
      ...progress.phase1.skippedSteps,
    ]);
    return PHASE1_STEPS.find((step) => !handled.has(step)) ?? null;
  },

  // Phase 2 (flat aliases for convenience)
  phase2Completed: (s: { onboarding?: OnboardingState }) =>
    s.onboarding?.progress?.phase2?.isCompleted ?? false,
  phase2Dismissed: (s: { onboarding?: OnboardingState }) =>
    s.onboarding?.progress?.phase2?.dismissedAt != null,
  phase2CompletedSteps: (s: { onboarding?: OnboardingState }) =>
    s.onboarding?.progress?.phase2?.completedSteps ?? [],
  phase2SkippedSteps: (s: { onboarding?: OnboardingState }) =>
    s.onboarding?.progress?.phase2?.skippedSteps ?? [],

  // Full progress object (raw access)
  progress: (s: { onboarding?: OnboardingState }) => s.onboarding?.progress,
  hasSampleData: (s: { onboarding?: OnboardingState }) => s.onboarding?.progress?.hasSampleData ?? false,
};